import { Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LegalPageLayout, {
  LegalSection as H2,
  LegalParagraph as P,
} from '@components/legal/LegalPageLayout';

const LAST_UPDATED = '27 October 2026';

/**
 * Privacy Policy (DRAFT). Public route /legal/privacy. The data-handling
 * description reflects the platform's actual practices; specific entity and
 * regulator details are flagged for counsel.
 */
function PrivacyPolicyPage() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout title={t('legal.privacy')} lastUpdated={LAST_UPDATED}>
      <P>
        This Privacy Policy explains how AFixt (the &ldquo;Company,&rdquo; &ldquo;we&rdquo;)
        collects, uses, and protects personal data when you use the accessibility-testing platform
        (the &ldquo;Service&rdquo;). Where we process personal data on behalf of a customer
        organization, we act as a processor under that customer&rsquo;s instructions; see the{' '}
        <Link component={RouterLink} to="/legal/dpa">
          {t('legal.dpa')}
        </Link>
        .
      </P>

      <H2>1. Data we collect</H2>
      <P>We collect and process the following categories of data:</P>
      <Typography variant="body1" component="ul">
        <li>
          <strong>Account data</strong> — name, email address, organization, role, and
          authentication metadata.
        </li>
        <li>
          <strong>Ingested content</strong> — HTML snapshots and screenshots of the pages you test,
          which may incidentally contain personal data present on those pages.
        </li>
        <li>
          <strong>Findings and evidence</strong> — accessibility results, reasoning, failing and
          passing code examples, and remediation notes.
        </li>
        <li>
          <strong>Attestation records</strong> — the identity of the human reviewer who attests a
          conformance snapshot, and the associated audit trail.
        </li>
        <li>
          <strong>Operational data</strong> — logs and request metadata used to run and secure the
          Service. Application logs do not contain personal data; infrastructure logs may contain IP
          addresses for security purposes.
        </li>
      </Typography>

      <H2>2. How we use data</H2>
      <P>
        We use data to provide the Service: running scans, generating findings and reports,
        supporting human attestation, tracking conformance over time, billing, and securing the
        platform. AI-assisted features route through our own audited AI proxy so prompts and
        responses can be logged per organization; we do not send your content to third-party AI
        services without explicit, per-action consent. The AI provider is Anthropic, which may not
        train models on customer content; an organization&rsquo;s Owners and Admins can switch AI
        features off entirely.
      </P>

      <H2>3. Legal bases</H2>
      <P>
        Where the GDPR applies, we rely on performance of a contract (to provide the Service), our
        legitimate interests (to secure and improve the Service), consent (where required, e.g. for
        certain integrations), and legal obligation. [Specific bases to be confirmed by counsel.]
      </P>

      <H2>4. Sharing and subprocessors</H2>
      <P>
        We do not sell personal data. We share data with vetted subprocessors that host or support
        the Service (for example, infrastructure and email providers), under contracts requiring
        appropriate protection, including Anthropic for the optional AI features where AFixt&rsquo;s
        own AI key is used (with an organization&rsquo;s own key, Anthropic acts under that
        organization&rsquo;s agreement with it). Our current{' '}
        <Link component={RouterLink} to="/legal/subprocessors">
          subprocessor list
        </Link>{' '}
        is published. Customer site content is not transmitted to third parties without explicit
        per-action customer consent.
      </P>

      <H2>5. Security</H2>
      <P>
        We apply technical and organizational measures appropriate to the risk, including:
        encryption of HTML snapshots and screenshots at rest; encryption in transit (HTTPS/HSTS);
        role-based access control; tenant isolation enforced at the database layer so cross-tenant
        access is not possible; and secret-management practices that keep credentials and key
        material out of logs and responses.
      </P>

      <H2>6. Retention and deletion</H2>
      <P>
        We retain data only as long as needed for the purposes above, under per-Context retention
        policies — there is no indefinite retention by default. Locked conformance snapshots are
        immutable evidence and are retained for the life of the relevant audit record. You may
        request deletion of your Context data at any time, and we honour data-subject erasure
        requests under GDPR Article 17, subject to legal-retention exceptions.
      </P>

      <H2>7. Your rights</H2>
      <P>
        Depending on your location, you may have rights to access, correct, delete, restrict, or
        port your personal data, and to object to certain processing. To exercise these rights,
        contact us at <Link href="mailto:privacy@afixt.com">privacy@afixt.com</Link>. Where we act
        as a processor for a customer, we will direct your request to that customer.
      </P>

      <H2>8. International transfers</H2>
      <P>
        Where personal data is transferred across borders, we use appropriate safeguards such as
        Standard Contractual Clauses. [Transfer mechanisms and hosting regions to be confirmed by
        counsel.]
      </P>

      <H2>9. Cookies</H2>
      <P>
        We use strictly necessary cookies to operate the Service (for example, authentication and
        session integrity). We do not use advertising cookies.
      </P>

      <H2>10. Contact</H2>
      <P>
        Questions about this Policy or our data practices can be sent to{' '}
        <Link href="mailto:privacy@afixt.com">privacy@afixt.com</Link>. [Data controller identity
        and any Data Protection Officer / EU representative details to be specified by counsel.]
      </P>
    </LegalPageLayout>
  );
}

export default PrivacyPolicyPage;
